import {
  Bot,
  Code2,
  Globe,
  LayoutDashboard,
  BrainCircuit,
  Users,
  Workflow,
  LifeBuoy,
  Target,
  Eye,
  Sparkles,
  Cpu,
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Home", id: "home" },
  { label: "About", id: "about" },
  { label: "Services", id: "services" },
  { label: "Solutions", id: "solutions" },
  { label: "Our Work", id: "our-work" },
  { label: "Contact", id: "contact" },
];

/*
 * OUR WORK — real client projects.
 *
 * To add a future project (e.g. Everything Clean Mobile Detailing, HC Apparel,
 * Plant Seeds Project), just append one object below. Only `url` is required
 * for the preview image — it is auto-generated as a live screenshot of the
 * site. Provide an optional `image` to override the auto screenshot.
 *
 * Fields:
 *   name          Project / client name
 *   category      Short category label (e.g. "Business Website")
 *   description   1–2 sentence summary
 *   technologies  Array of tech / tools used
 *   url           Live website URL (opens via "Visit Website")
 *   image         (optional) custom preview image URL
 */
export const WORK = [
  {
    id: "attitude-of-service",
    name: "Attitude of Service",
    category: "Business Website",
    description:
      "A modern, responsive website developed for Attitude of Service to establish a professional online presence, improve customer engagement, and streamline online communication.",
    technologies: ["React", "TypeScript", "Tailwind CSS", "AI", "Cloudflare", "Google Workspace"],
    url: "https://www.attitudeofservice.com",
  },
];

export const STATS = [
  { label: "Projects Completed", value: 120, suffix: "+" },
  { label: "Automation Hours Saved", value: 18000, suffix: "+" },
  { label: "Client Satisfaction", value: 98, suffix: "%" },
  { label: "Systems Built", value: 60, suffix: "+" },
];

/*
 * Eight service categories. Each has a clear business BENEFIT, a short
 * explanation, and drives a "Schedule a Consultation" CTA.
 */
export const SERVICES = [
  {
    id: "ai-automation",
    icon: Bot,
    title: "AI Automation",
    benefit: "Cut operating costs & manual work",
    desc: "We automate repetitive operations end-to-end so your team focuses on high-value decisions instead of busywork.",
  },
  {
    id: "custom-software-development",
    icon: Code2,
    title: "Custom Software Development",
    benefit: "Software built around your business",
    desc: "Bespoke applications engineered to match exactly how your business runs — scalable, secure, and maintainable.",
  },
  {
    id: "intelligent-websites",
    icon: Globe,
    title: "Intelligent Websites",
    benefit: "Convert more of your traffic",
    desc: "Fast, elegant, AI-enhanced websites designed to turn visitors into qualified customers and measurable growth.",
  },
  {
    id: "business-dashboards",
    icon: LayoutDashboard,
    title: "Business Dashboards",
    benefit: "Decide with real-time clarity",
    desc: "Live control centers that turn scattered data into clear, actionable intelligence for faster decisions.",
  },
  {
    id: "ai-agents",
    icon: BrainCircuit,
    title: "AI Agents",
    benefit: "Scale capacity around the clock",
    desc: "Goal-driven agents that reason, act, and integrate with your systems to extend your team 24/7.",
  },
  {
    id: "crm-and-business-systems",
    icon: Users,
    title: "CRM & Business Systems",
    benefit: "Unify customer operations",
    desc: "Tailored CRM and internal systems that centralize relationships, pipelines, and workflows in one place.",
  },
  {
    id: "api-and-workflow-integrations",
    icon: Workflow,
    title: "API & Workflow Integrations",
    benefit: "Connect every tool you use",
    desc: "Seamlessly integrate your stack into one reliable ecosystem, eliminating silos and manual handoffs.",
  },
  {
    id: "ongoing-support-and-optimization",
    icon: LifeBuoy,
    title: "Ongoing Support & Optimization",
    benefit: "Systems that keep improving",
    desc: "Continuous monitoring, refinement, and optimization so your software compounds value over time.",
  },
];

/*
 * Professional PLACEHOLDER case studies representing the solution types
 * Métis Tech Labs builds. `featured: true` items appear under "Featured
 * Projects"; the rest under "Case Studies". Structured so real client
 * projects can replace each entry field-for-field.
 */
export const SOLUTIONS = [
  {
    id: "ai-operations-automation",
    title: "AI Operations Automation",
    category: "AI Automation",
    featured: true,
    placeholder: true,
    summary:
      "Autonomous pipelines that replaced dozens of manual operational steps across finance and logistics.",
    challenge:
      "Repetitive manual processes consumed thousands of hours each year and introduced costly errors.",
    solution:
      "We modeled the workflows and automated them with resilient, event-driven pipelines and full observability.",
    image:
      "https://images.pexels.com/photos/31032752/pexels-photo-31032752.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    stack: ["Python", "Event Queues", "Serverless", "Monitoring"],
    results: ["18k hours saved / year", "Zero-touch operations"],
  },
  {
    id: "enterprise-analytics-dashboard",
    title: "Enterprise Operations Dashboard",
    category: "Enterprise Dashboards",
    featured: true,
    placeholder: true,
    summary:
      "A unified, real-time command center consolidating telemetry across distributed teams and facilities.",
    challenge:
      "Leaders relied on a dozen disconnected tools with no single source of operational truth.",
    solution:
      "We built a live dashboard with streaming metrics, predictive forecasting, and anomaly alerts.",
    image:
      "https://images.pexels.com/photos/28901526/pexels-photo-28901526.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    stack: ["React", "FastAPI", "WebSockets", "TimescaleDB"],
    results: ["+41% throughput", "-63% incident response time"],
  },
  {
    id: "autonomous-ai-agents",
    title: "Autonomous Support Agents",
    category: "AI Agents",
    featured: true,
    placeholder: true,
    summary:
      "Reasoning AI agents that resolve tier-1 requests autonomously and escalate with full context.",
    challenge:
      "Support volume outpaced headcount, threatening response times and customer retention.",
    solution:
      "We deployed agents integrated with the knowledge base and ticketing system, with seamless human handoff.",
    image:
      "https://images.pexels.com/photos/30767251/pexels-photo-30767251.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    stack: ["LLM Orchestration", "Vector Search", "RAG", "Python"],
    results: ["78% auto-resolved", "24/7 coverage"],
  },
  {
    id: "crm-platform-development",
    title: "Custom CRM Platform",
    category: "CRM Development",
    featured: false,
    placeholder: true,
    summary:
      "A tailored CRM that centralized pipelines, communications, and reporting for a growing sales org.",
    challenge:
      "Off-the-shelf CRM tooling forced the team into workflows that didn't match their process.",
    solution:
      "We built a bespoke CRM mapped to their exact sales motion, with automation and clean reporting.",
    image:
      "https://images.pexels.com/photos/12707786/pexels-photo-12707786.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    stack: ["React", "Node", "PostgreSQL", "Automation"],
    results: ["+29% pipeline visibility", "Single source of truth"],
  },
  {
    id: "high-conversion-website",
    title: "High-Conversion Product Website",
    category: "Custom Websites",
    featured: false,
    placeholder: true,
    summary:
      "A fast, personalized marketing platform that lifted conversions and told a sophisticated product story.",
    challenge:
      "A dated site loaded slowly and failed to communicate the product's value to enterprise buyers.",
    solution:
      "We rebuilt on an edge-rendered stack with a headless CMS and per-segment personalization.",
    image:
      "https://images.pexels.com/photos/31032752/pexels-photo-31032752.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    stack: ["React", "Edge Rendering", "Headless CMS"],
    results: ["+2.4x conversions", "98 Lighthouse score"],
  },
  {
    id: "workflow-integration-suite",
    title: "Workflow Integration Suite",
    category: "Workflow Integrations",
    featured: false,
    placeholder: true,
    summary:
      "Connected a fragmented toolset into one reliable ecosystem, eliminating manual data handoffs.",
    challenge:
      "Critical data lived in disconnected apps, forcing error-prone manual re-entry between teams.",
    solution:
      "We integrated the stack via APIs with automated syncing, validation, and failure recovery.",
    image:
      "https://images.pexels.com/photos/28901526/pexels-photo-28901526.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    stack: ["REST APIs", "Webhooks", "Queues"],
    results: ["100% synced systems", "-90% manual entry"],
  },
];

export const PROJECT_TYPES = [
  "AI Automation",
  "Custom Software",
  "Intelligent Website",
  "Business Dashboard",
  "AI Agents",
  "CRM & Business Systems",
  "API & Workflow Integration",
  "Ongoing Support",
  "Other",
];

export const BUDGETS = [
  "< $10k",
  "$10k \u2013 $25k",
  "$25k \u2013 $50k",
  "$50k \u2013 $100k",
  "$100k+",
];

export const VALUES = [
  {
    title: "Mission",
    desc: "Help businesses automate operations and build intelligent digital solutions that create durable advantage.",
    icon: Target,
  },
  {
    title: "Vision",
    desc: "A future where every company runs on software that thinks and adapts alongside its team.",
    icon: Eye,
  },
  {
    title: "Innovation",
    desc: "We apply frontier AI as a practical, measured tool that solves real business problems \u2014 never hype.",
    icon: Sparkles,
  },
  {
    title: "Engineering",
    desc: "Disciplined, production-grade delivery: integrated with your stack and accountable to outcomes.",
    icon: Cpu,
  },
];
